import { usePrevious } from "@react-hookz/web";
import { useEffect } from "react";
import { isCreatableMarkerOption } from "../utils";

/**
 * Hook to handle setting appropriate highlight index when options are loading.
 * Does nothing if selectFirstAutomatically is false.
 */
export const useAutoHighlightWithLoading = <T>(
    setHighlightedIndex: (index: number) => void,
    options: T[],
    isLoading: boolean,
    selectFirstAutomatically: boolean,
    isCreatable: boolean,
    input: string,
    shouldNotAutoHighlightAfterSelect = false
) => {
    const previousOptions = usePrevious(options);
    useEffect(() => {
        if (selectFirstAutomatically) {
            if (isLoading) {
                setHighlightedIndex(-1);
            } else {
                if (shouldNotAutoHighlightAfterSelect && input.length === 0) {
                    // if shouldNotAutoHighlightAfterSelect is true, don't auto-highlight anything if there is no current search value
                    //  (normally we highlight the "number selected" option in the dropdown when the user selects something)
                    setHighlightedIndex(-1);
                } else {
                    // only update highlighting if the list of options has changed
                    if (options !== previousOptions) {
                        const hasCreatableOption =
                            isCreatable && options.length > 0 && isCreatableMarkerOption(options[0]);
                        const hasNonCreatableOption =
                            options && (hasCreatableOption ? options.length > 1 : options.length > 0);
                        // if we have a Create Option, start there. Otherwise, assume we are going to highlight nothing.
                        let defaultHighlightIndex = hasCreatableOption ? 0 : -1;
                        // do we have a pre-existing option the user can select?
                        if (hasNonCreatableOption) {
                            // go to first pre-existing option instead of Create
                            defaultHighlightIndex += 1;
                        }
                        setHighlightedIndex(defaultHighlightIndex);
                    }
                }
            }
        }
    }, [
        input.length,
        isCreatable,
        isLoading,
        options,
        previousOptions,
        selectFirstAutomatically,
        setHighlightedIndex,
        shouldNotAutoHighlightAfterSelect,
    ]);
};
